import React, { ReactElement, ReactNode } from "react";

import FixedBottomIcon from "@src/assets/icons/fixedbottom-tech.svg";
import FloatingIcon from "@src/assets/icons/floating-tech.svg";
import HydrogenIcon from "@src/assets/icons/hydrogen.svg";
import "./project_information_renderer.less";
import ProjectPhaseChip from "@src/components/project_phase_chip/project_phase_chip";
import { DIGIX_TECHNOLOGY, DUALIS_TECHNOLOGY } from "@src/constants";
import { ProjectInformationRendererValue } from "@src/types/custom_renderer_types";
import { CustomCellRendererProps } from "ag-grid-react";
import { Tooltip } from "antd";
import _ from "lodash";
import { EmployeeName } from "@src/components/employee_name/index";

interface ProjectInformationRendererParams {
  props: CustomCellRendererProps;
}

const ProjectInformationRenderer: React.FC<
  ProjectInformationRendererParams
> = ({ props }) => {
  const {
    projectName,
    projectPhase,
    technology,
    projectLeadEmployeeName,
    isHydrogen,
  }: ProjectInformationRendererValue = props.value;

  /**
   * This function will not be needed or at least changed when digiX is fully implemented. So this is a workaround to make it still work with Dualis projects.
   *
   * DigiX technologies are always one single word like "Fixed", "Floating" or "Mixed".
   * Hydrogen is used as an own field on the project and not as a technology.
   * @returns
   */
  function getTechnologyTooltipText(): string {
    if (isHydrogen && !technology) return DUALIS_TECHNOLOGY.HYDROGEN;

    const technologies: string[] = technology.split(",");

    const capitalizedTechnologies = technologies.map((technology) =>
      _.capitalize(technology)
    );

    if (isHydrogen) {
      capitalizedTechnologies.push(DUALIS_TECHNOLOGY.HYDROGEN);
    }

    return capitalizedTechnologies.join(", ");
  }

  // when digiX is fully implemented, we probably need to add hydrogen as an own property and not inside of the technologies
  function getTooltipText(): ReactNode {
    return (
      <div className="project-info__tooltip">
        <p>{`Project Name: ${projectName}`}</p>
        <p>{`Project Phase: ${projectPhase}`}</p>
        {(technology || isHydrogen) && (
          <p>{`Technologies: ${getTechnologyTooltipText()}`}</p>
        )}
        <p>{`Project Lead: ${projectLeadEmployeeName}`}</p>
      </div>
    );
  }

  /**
   * Renders the right icon based on the technology
   *
   * @returns the right icon or undefined
   */
  function renderTechnologyIcon(): ReactElement | undefined {
    switch (technology) {
      case DUALIS_TECHNOLOGY.FLOATING:
      case DIGIX_TECHNOLOGY.FLOATING:
        return (
          <FloatingIcon
            className="project-info__technology-icon"
            data-testid="technology-icon-floating"
          />
        );
      case DUALIS_TECHNOLOGY.FIXED_BOTTOM:
      case DIGIX_TECHNOLOGY.FIXED_BOTTOM:
        return (
          <FixedBottomIcon
            className="project-info__technology-icon"
            data-testid="technology-icon-fixed"
          />
        );
      default:
        return;
    }
  }

  return (
    <Tooltip
      classNames={{ root: "project-info__tooltip" }}
      placement="bottomLeft"
      mouseEnterDelay={0.5}
      title={getTooltipText()}
    >
      <div className="project-info" data-testid="project-info">
        <div className="project-info__wrapper">
          <ProjectPhaseChip projectPhase={projectPhase} />
          {technology && renderTechnologyIcon()}
          <div>
            <span className="project-info__name-label">{projectName}</span>
            <div className="project-info__subtext">
              <span>{technology && `${_.capitalize(technology)} ·`}</span>
              <span>
                <EmployeeName name={projectLeadEmployeeName} />
              </span>
            </div>
          </div>
        </div>
        <div>
          {isHydrogen && (
            <HydrogenIcon
              className="project-info__hydrogen-icon"
              data-testid="hydrogen-icon"
            />
          )}
        </div>
      </div>
    </Tooltip>
  );
};

export default ProjectInformationRenderer;
